import { useEffect, useState, useRef, useCallback } from "react";
import { useRecoilState } from "recoil";
import { useNavigate } from "react-router-dom";
import { Input, SreenCoverLoading, Modal } from "src/components";
// TODO: 모달 컴포넌트 변경
import { useAlert, useGroupPosition } from "src/hooks";
import AlertModal from "src/components/YearBooking/components/common/AlertModal";
import { createTeeshotAxiosInstance } from "src/http";
import { recoilStates } from "src/recoil/recoilStates";

import styled from "styled-components";
import { MemberType } from "src/types";
import { usePopupSelectV2 } from "src/components/PopupSelect";
import teeshotMemberIcon from "src/assets/images/teeshot-member-icon.svg";

function YearGroupMembersList() {
  const navigate = useNavigate();
  const [group, setGroup] = useRecoilState(recoilStates.group);
  const [user] = useRecoilState(recoilStates.user);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadMembersModalShown, setIsLoadMembersModalShown] = useState(false);
  const positions = useGroupPosition();

  // 멤버 카드 영역을 위한 ref
  const memberCardsRef = useRef<HTMLDivElement>(null);

  // AlertModal 상태 관리
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showEditErrorModal, setShowEditErrorModal] = useState(false);
  const [showNaverBandModal, setShowNaverBandModal] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showPresidentLimitModal, setShowPresidentLimitModal] = useState(false);
  const addFormRef = useRef<HTMLDivElement>(null);
  const {
    value: nameValue,
    onChange: handleNameChange,
    resetValue: resetName,
  } = Input.hooks.useInput();
  const {
    value: contactValue,
    onChange: handleContactChange,
    resetValue: resetContact,
  } = Input.hooks.useInput("");
  // TODO 폰 번호형식 유효성검사
  // 직책 선택을 위한 상태 관리
  const [selectedPositionId, setSelectedPositionId] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".custom-dropdown")) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const [isMemberEditModalShown, setIsMemberEditModalShown] = useState(false);
  const [editTargetMember, setEditTargetMember] = useState<MemberType | null>(null);

  const {
    PopupSelect: EditPopupSelect,
    activeIds: activeEditingMemberLevelIds,
    propsToInject: editPropsToInject,
  } = usePopupSelectV2({
    defaultSelectIds: editTargetMember ? [editTargetMember.booking_group_member_level_id] : [2],
  });

  const { value: editingMemberNameValue, onChange: handleChangeEditingMemberNameValue } =
    Input.hooks.useInput();
  const { value: editingMemberContactValue, onChange: handleChangeEditingMemberContactValue } =
    Input.hooks.useInput();

  useEffect(() => {
    // TODO Input 컴포넌트 수정 후 리팩토링
    handleChangeEditingMemberNameValue({
      target: { value: editTargetMember?.name },
    } as any);
    handleChangeEditingMemberContactValue({
      target: { value: editTargetMember?.phone_number },
    } as any);
  }, [editTargetMember]);

  useEffect(() => {
    if (positions) {
      setIsLoading(false);
    }
  }, [positions]);

  // ionic 앱에서 보낸 메시지 처리
  const isProcessingContactsRef = useRef(false);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // 중복 처리를 막기 위해, 현재 연락처를 처리 중이면 함수 종료
      if (isProcessingContactsRef.current) return;

      let data: any = null;

      if (typeof event.data === "string") {
        try {
          data = JSON.parse(event.data);
        } catch (e) {
          data = event.data;
        }
      } else if (typeof event.data === "object" && event.data !== null) {
        data = event.data;
      } else {
        data = event.data;
      }

      if (data && data.type === "contactsList") {
        isProcessingContactsRef.current = true;
        const contactData = data.contacts.contact;
        const contactName = contactData.name.display;

        let contactPhones: any[] = [];
        // 연락처 type 이 'mobile' 인 번호가 여러개일 경우에는 1개만 반영
        contactPhones = Array.isArray(contactData.phones)
          ? contactData.phones.filter((phone: any) => phone.type === "mobile")
          : [];
        if (contactPhones.length > 1) {
          contactPhones = [contactPhones[0]];
        }
        // 연락처 등록 멤버 처리 (동기화 문제 방지가 핵심)
        const processContactMembers = async () => {
          setIsLoading(true);
          // 멤버 목록을 핸들링 시점에 snapshot으로 가져온다.
          // 항상 최신 멤버정보 획득을 위해 해당 함수 내에서 최신 멤버 상태 사용
          let latestMembers: MemberType[] = [];
          setGroup((prev) => {
            latestMembers = prev.members || [];
            return prev;
          });

          for (const phone of contactPhones) {
            const phoneNumber = phone.number;

            // 중복 체크 - 이미 등록된 전화번호인지 확인
            // 최신 members 배열로 체크
            const alreadyExists = latestMembers.find(
              (member) => member.phone_number === phoneNumber
            );
            if (alreadyExists) {
              continue;
            }

            try {
              const submitData = {
                groupId: group.id,
                name: contactName.replace(/[^a-zA-Zㄱ-ㅎㅏ-ㅣ가-힣0-9]/g, ""), // 특수문자 제거
                contact: phoneNumber.replace(/[^0-9]/g, ""), // 숫자만 반영
                levelId: 3, // 기본값 : 일반 멤버
              };

              const res = await createTeeshotAxiosInstance().post("/yearBooking/member", {
                submitData,
              });

              if (res?.data?.data && res.data.success !== false) {
                // 성공적으로 등록된 멤버를 group.members에 추가
                setGroup((prev) => ({
                  ...prev,
                  members: [...(prev.members || []), res.data.data],
                }));
                // 등록된 members에 반영 (이미 추가된 번호 등록 방지)
                latestMembers.push(res.data.data);
              }
            } catch (err) {
              // 에러 무시 (알림은 생략)
            }
          }

          setIsLoading(false);
          isProcessingContactsRef.current = false;
        };

        processContactMembers();
      }
    },
    // setGroup만 의존. group(멤버정보)는 함수 내에서 직접 snapshot하여 사용
    [setGroup, group.id, setIsLoading]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  const alert = useAlert();
  const handleAddMember = () => {
    if (!nameValue || !contactValue) {
      setShowValidationModal(true);
      return;
    }

    // 회장(id: 1) 중복 체크
    const selectedLevel = selectedPositionId ?? 3;
    if (selectedLevel === 1) {
      const existingPresident = group.members?.find((m) => m.booking_group_member_level_id === 1);
      if (existingPresident) {
        setShowPresidentLimitModal(true);
        return;
      }
    }

    (async () => {
      setIsLoading(true);
      const submitData = {
        groupId: group.id,
        name: nameValue,
        contact: contactValue,
        levelId: selectedLevel,
      };
      try {
        const res = await createTeeshotAxiosInstance().post("/yearBooking/member", {
          submitData,
        });
        if (!res?.data?.data || res.data.success === false) {
          throw new Error("오류");
        }
        setIsLoading(false);
        resetName();
        resetContact();
        setSelectedPositionId(null);
        setIsDropdownOpen(false);
        setGroup({
          ...group,
          members: [...group.members, res.data.data],
        });
        setShowAddMemberModal(false);
      } catch (e) {
        setShowErrorModal(true);
        console.log(" e:", e);
      }
    })();
  };
  // console.log('group :', group.group_code);
  const handleKakaoShare = () => {
    const windowObj: any = window;
    const handler: any = windowObj.webkit?.messageHandlers?.cordova_iab;
    const post = {
      type: "sendLinkFeed",
      button_title: "멤버 등록하기",
      description: `${group.name}의 연단체 멤버로 함께 하세요!
티샷 앱에서 간편하게 멤버 등록을 할 수 있어요!

✅ 서류 없이 1분 등록
✅ 라운딩 일정 앱에서 확인 가능

👉 지금 바로 티샷 앱에서 등록하세요!`,
      image_link_url: `${process.env.REACT_APP_DOMAIN}/loadApp/${group.group_code}`,
      image_url: "https://api.v2.teeshot.co.kr/event/resource/images/yb_invite2.png",
      title: `📢 ${user.realname}님이 티샷 연단체 멤버로 초대했습니다! ⛳`,
    };

    if (handler) {
      handler.postMessage(JSON.stringify(post));
    }
  };

  const handleLoadMembers = () => {
    setIsLoadMembersModalShown(true);
  };

  const handleImportFromKakao = () => {
    handleKakaoShare();
    setIsLoadMembersModalShown(false);
  };

  const handleImportFromNaverBand = () => {
    setShowNaverBandModal(true);
    setIsLoadMembersModalShown(false);
  };

  const handleImportFromContacts = () => {
    const windowObj: any = window;
    const handler: any = windowObj.webkit?.messageHandlers?.cordova_iab;

    if (handler) {
      handler.postMessage(JSON.stringify({ type: "openContacts" }));
    }
    // setShowContactsModal(true);
    setIsLoadMembersModalShown(false);
  };

  return (
    <>
      {isMemberEditModalShown && (
        <Modal
          setModalState={setIsMemberEditModalShown}
          onConfirm={() => {
            const submitData = {
              id: editTargetMember?.id,
              name: editingMemberNameValue.trim(),
              contact: editingMemberContactValue.trim(),
              levelId: activeEditingMemberLevelIds?.[0] ?? 3,
            };
            if (!submitData?.id || !submitData?.name || !submitData?.contact) {
              setShowValidationModal(true);
              return;
            }

            // 회장(id: 1) 중복 체크
            if (submitData.levelId === 1) {
              const existingPresident = group.members?.find(
                (m) => m.booking_group_member_level_id === 1 && m.id !== editTargetMember?.id
              );
              if (existingPresident) {
                setShowPresidentLimitModal(true);
                setIsMemberEditModalShown(false);
                return;
              }
            }

            (async () => {
              setIsLoading(true);
              try {
                const res = await createTeeshotAxiosInstance().put("/yearBooking/member", {
                  submitData,
                });
                if (!res.data.success) {
                  throw new Error();
                }
                setGroup((prev) => {
                  return { ...prev, members: res.data?.data?.members };
                });
              } catch (e) {
                setShowEditErrorModal(true);
                console.log("e :", e);
              } finally {
                setIsLoading(false);
              }
            })();
          }}
          content={
            <>
              <div className="flex-1 page-pd d-flex fd-c gap-2">
                <div>
                  <EditPopupSelect
                    {...editPropsToInject}
                    items={positions ?? []}
                    label="직책"
                    placeholder="직책"
                  />
                </div>
                <div>
                  <Input
                    label="이름"
                    attr={{
                      value: editingMemberNameValue,
                      onChange: handleChangeEditingMemberNameValue,
                      placeholder: "예시) 홍길동",
                    }}
                  />
                </div>
                <div>
                  <Input
                    label="전화번호"
                    attr={{
                      type: "number",
                      value: editingMemberContactValue,
                      onChange: handleChangeEditingMemberContactValue,
                      placeholder: "예시) 01012345678",
                    }}
                  />
                </div>
              </div>
            </>
          }
        />
      )}

      {isLoadMembersModalShown && (
        <>
          <LoadMembersOverlay onClick={() => setIsLoadMembersModalShown(false)} />
          <LoadMembersModal>
            <LoadMembersContent>
              <div className="modal-header">
                <h1 className="page-title">멤버 간편등록</h1>
                <button className="close-button" onClick={() => setIsLoadMembersModalShown(false)}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="#1A1A1A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <div className="import-options">
                <button className="import-option" onClick={handleImportFromKakao}>
                  <img
                    className="option-icon"
                    src="https://api.builder.io/api/v1/image/assets/TEMP/e9e7339edd74ef21ea045098c867578b6723f84f?width=80"
                    alt="KakaoTalk"
                  />
                  <span className="option-text">카카오톡에서 멤버 불러오기</span>
                </button>

                <button className="import-option" onClick={handleImportFromNaverBand}>
                  <img
                    className="option-icon"
                    src="https://api.builder.io/api/v1/image/assets/TEMP/b2f3363850d2bca403e71522892045f2fb9c7511?width=80"
                    alt="Naver Band"
                  />
                  <span className="option-text">네이버 밴드에서 멤버 불러오기</span>
                </button>

                <button className="import-option" onClick={handleImportFromContacts}>
                  <div className="contacts-icon">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="40" height="40" rx="6" fill="#007BFF" />
                      <path
                        d="M20 20C18.625 20 17.4479 19.5104 16.4688 18.5313C15.4896 17.5521 15 16.375 15 15C15 13.625 15.4896 12.4479 16.4688 11.4688C17.4479 10.4896 18.625 10 20 10C21.375 10 22.5521 10.4896 23.5312 11.4688C24.5104 12.4479 25 13.625 25 15C25 16.375 24.5104 17.5521 23.5312 18.5313C22.5521 19.5104 21.375 20 20 20ZM11 30C10.4477 30 10 29.5523 10 29V26.5C10 25.7917 10.1823 25.1406 10.5469 24.5469C10.9115 23.9531 11.3958 23.5 12 23.1875C13.2917 22.5417 14.6042 22.0573 15.9375 21.7344C17.2708 21.4115 18.625 21.25 20 21.25C21.375 21.25 22.7292 21.4115 24.0625 21.7344C25.3958 22.0573 26.7083 22.5417 28 23.1875C28.6042 23.5 29.0885 23.9531 29.4531 24.5469C29.8177 25.1406 30 25.7917 30 26.5V29C30 29.5523 29.5523 30 29 30H11Z"
                        fill="#FDFDFD"
                      />
                    </svg>
                  </div>
                  <span className="option-text">휴대폰 연락처에서 멤버 불러오기</span>
                </button>
              </div>
            </LoadMembersContent>
          </LoadMembersModal>
        </>
      )}

      {showAddMemberModal && (
        <>
          <MemberRegisterOverlay onClick={() => setShowAddMemberModal(false)} />
          <MemberRegisterBottomSheet>
            <MemberRegisterContent>
              <div className="modal-header">
                <h1 className="modal-title">멤버 등록</h1>
                <button className="close-button" onClick={() => setShowAddMemberModal(false)}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="#1A1A1A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <div className="form-container">
                <div className="form-field">
                  <label className="field-label">직책</label>
                  <div className={`custom-dropdown ${selectedPositionId ? "has-value" : ""}`}>
                    <div
                      className="dropdown-trigger"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                      <span className="dropdown-text">
                        {selectedPositionId
                          ? positions?.find((p) => p.id === selectedPositionId)?.name ||
                            "직책을 선택해주세요"
                          : "직책을 선택해주세요"}
                      </span>
                      <svg
                        className={`dropdown-arrow ${isDropdownOpen ? "open" : ""}`}
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="#6b7280"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>

                    {isDropdownOpen && (
                      <div className="dropdown-options">
                        {positions?.map((position) => (
                          <div
                            key={position.id}
                            className={`dropdown-option ${
                              selectedPositionId === position.id ? "selected" : ""
                            }`}
                            onClick={() => {
                              setSelectedPositionId(position.id);
                              setIsDropdownOpen(false);
                            }}
                          >
                            {position.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-field">
                  <label className="field-label">이름</label>
                  <input
                    className={`form-input ${nameValue ? "has-value" : ""}`}
                    value={nameValue}
                    onChange={handleNameChange}
                    placeholder="이름을 입력해주세요"
                  />
                </div>

                <div className="form-field contact-field">
                  <label className="field-label">연락처</label>
                  <input
                    className={`form-input ${contactValue ? "has-value" : ""}`}
                    type="tel"
                    value={contactValue}
                    onChange={handleContactChange}
                    placeholder="연락처를 입력해주세요"
                  />
                </div>
              </div>

              <button className="register-button" onClick={handleAddMember}>
                멤버 등록
              </button>
            </MemberRegisterContent>
          </MemberRegisterBottomSheet>
        </>
      )}

      <StyledPage className="member-management-page">
        <div className="info-banner">
          <div className="banner-text">멤버를 등록하고, 조를 편성하여 제출해주세요.</div>
        </div>

        <div className="section-header">
          <div className="section-title">멤버등록</div>
          <div className="member-count">
            <span className="count-label">멤버 수 </span>
            <span className="count-number">{group.members?.length || 0}명</span>
          </div>
        </div>

        <div className="member-cards" ref={memberCardsRef}>
          {group.members?.length > 0 ? (
            group.members.map((m) => {
              return (
                <div
                  className="member-card"
                  key={m.id}
                  onClick={() => {
                    setEditTargetMember(m);
                    setTimeout(() => {
                      setIsMemberEditModalShown(true);
                    }, 0);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className="member-info">
                    <div className="member-position">
                      {positions?.find((p) => p.id === m.booking_group_member_level_id)?.name ||
                        "총무"}
                    </div>
                    <div className="member-name">{m.name}</div>
                    <div className="member-phone">{m.phone_number}</div>
                    <div className="member-status">
                      {m.teeshot_user_id ? (
                        <img src={teeshotMemberIcon} alt="티샷 회원" width="24" height="24" />
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <></>
          )}
        </div>

        <div className="bottom-section">
          <div className="button-container">
            <button
              className="member-register-button"
              onClick={() => {
                setShowAddMemberModal(true);
              }}
            >
              멤버 등록
            </button>
            <button className="easy-register-button" onClick={handleLoadMembers}>
              간편 등록
            </button>
          </div>
        </div>
      </StyledPage>

      {/* AlertModals */}
      {showValidationModal && (
        <AlertModal
          onClose={() => setShowValidationModal(false)}
          title="입력 오류"
          subtitle="이름과 연락처를 입력하세요."
          lines={["필수 입력 항목을 확인해주세요."]}
          confirmText="확인"
          onConfirm={() => setShowValidationModal(false)}
        />
      )}

      {showErrorModal && (
        <AlertModal
          onClose={() => setShowErrorModal(false)}
          title="오류 발생"
          subtitle="멤버 등록 중 오류가 발생했습니다."
          lines={["잠시 후 다시 시도해주세요."]}
          confirmText="확인"
          onConfirm={() => {
            setShowErrorModal(false);
            window.location.reload();
          }}
        />
      )}

      {showEditErrorModal && (
        <AlertModal
          onClose={() => setShowEditErrorModal(false)}
          title="수정 실패"
          subtitle="멤버 정보 수정에 실패했습니다."
          lines={["잠시 후 다시 시도해주세요."]}
          confirmText="확인"
          onConfirm={() => {
            setShowEditErrorModal(false);
            window.location.reload();
          }}
        />
      )}

      {showNaverBandModal && (
        <AlertModal
          onClose={() => setShowNaverBandModal(false)}
          title="준비 중"
          subtitle="네이버 밴드에서 멤버 불러오기 기능은 준비 중입니다."
          lines={["곧 서비스될 예정입니다."]}
          confirmText="확인"
          onConfirm={() => setShowNaverBandModal(false)}
        />
      )}

      {showContactsModal && (
        <AlertModal
          onClose={() => setShowContactsModal(false)}
          title="준비 중"
          subtitle="휴대폰 연락처에서 멤버 불러오기 기능은 준비 중입니다."
          lines={["곧 서비스될 예정입니다."]}
          confirmText="확인"
          onConfirm={() => setShowContactsModal(false)}
        />
      )}

      {showPresidentLimitModal && (
        <AlertModal
          onClose={() => setShowPresidentLimitModal(false)}
          title="회장 등록 제한"
          subtitle="회장 등록은 1명만 가능합니다."
          lines={["이미 회장으로 등록된 멤버가 있습니다.", "다른 직책을 선택해주세요."]}
          confirmText="확인"
          onConfirm={() => setShowPresidentLimitModal(false)}
        />
      )}

      {isLoading && <SreenCoverLoading />}
    </>
  );
}

export default YearGroupMembersList;

const LoadMembersModal = styled.div`
  position: fixed;
  bottom: 0; /* 하단에 완전히 붙게 */
  left: 0;
  right: 0;
  z-index: 11000000; /* 가장 높은 z-index로 설정 */
  display: flex;
  justify-content: center;
  pointer-events: auto; /* 모달 자체는 클릭 가능하도록 */
`;

const LoadMembersContent = styled.div`
  width: 100%;
  height: 379px;
  background: var(--teeshot-white, #fff);
  border-radius: 10px 10px 0 0;
  padding: 24px 16px 0 16px;
  position: relative;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  animation: slideUpFromButton 0.3s ease-out;
  pointer-events: auto; /* 모달 내용은 클릭 가능하도록 */

  @keyframes slideUpFromButton {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
  }

  .close-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f0f0f0;
    }
  }

  .page-title {
    color: var(--teeshot-black, #1a1a1a);
    font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
    margin: 0;
    width: auto;
    height: auto;
  }

  .import-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .import-option {
    display: flex;
    width: 100%;
    height: 72px;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border-radius: 6px;
    border: 1px solid var(--teeshot-g2, #d1d5d8);
    background: var(--teeshot-white, #fff);
    cursor: pointer;
    transition: border-color 0.2s ease;
    pointer-events: auto; /* 버튼은 클릭 가능하도록 */

    &:hover {
      border-color: var(--teeshot-active, #007bff);
    }
  }

  .option-icon,
  .contacts-icon {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    flex-shrink: 0;
  }

  .option-text {
    color: var(--teeshot-black, #1a1a1a);
    font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    flex: 1;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  pointer-events: auto; /* 오버레이는 클릭 가능하도록 (모달 닫기용) */
`;

const StyledPage = styled.div`
  padding: 10px;
  gap: 12px;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh; /* 전체 화면 높이 사용 */
  background: var(--teeshot-white, #fff);
  position: relative;
  margin: 0 auto;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  overflow: hidden; /* 글로벌 스크롤 제거, 이 페이지에서만 */

  .info-banner {
    display: flex;
    width: 100%;
    padding: 8px 12px;
    align-items: center;
    gap: 10px;
    border-radius: 6px;
    background: #dbedff;
    height: 37px;
    position: sticky;
    top: 0;
    background: #dbedff;
    margin: 14px 0;
  }
  .banner-text {
    width: 334px;
    color: var(--teeshot-active, #007bff);
    font-size: 14px;
    font-weight: 500;
    line-height: 150%;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 18px;
    position: sticky;
    top: 45px; /* info-banner 높이 + 여백 */
    background: var(--teeshot-white, #fff);
    padding: 10px 0;
  }
  .section-title {
    color: var(--teeshot-black, #1a1a1a);
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
  }
  .member-count {
    color: var(--teeshot-black, #1a1a1a);
    text-align: right;
    font-size: 14px;
    line-height: 18px;
  }
  .count-label {
    font-weight: 400;
  }
  .count-number {
    font-weight: 700;
  }

  .member-cards {
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: start;
    align-items: space-between;
    gap: 8px;
    margin-top: 12px;
    padding-bottom: 120px; /* 하단 버튼 공간 확보 */
    overflow-y: auto;
    flex: 1; /* 남은 공간을 모두 차지 */
    min-height: 0; /* flex 아이템이 축소될 수 있도록 */
  }
  .member-card {
    border-radius: 6px;
    border: 1px solid var(--Teeshot-Neutrals-Color-Teeshot-G2, #d1d5d8);
    background: var(--Teeshot-Neutrals-Color-Teeshot-White, #fdfdfd);
  }
  .member-info {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    width: 100%;
    gap: 4px;
    justify-content: space-between;
  }
  .member-position {
    color: var(--teeshot-black, #1a1a1a);
    font-size: 14px;
    font-weight: 600;
    min-width: 50px;
  }
  .member-name {
    color: var(--teeshot-black, #1a1a1a);
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    min-width: 90px;
  }
  .member-phone {
    color: var(--teeshot-g1, #6b7280);
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    min-width: 100px;
  }
  .member-status {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    min-width: 25px;

    img {
      display: block;
    }
  }

  /* Bottom Section */
  .bottom-section {
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    padding: 26px 16px;
    background: linear-gradient(
      0deg,
      var(--teeshot-white, #fdfdfd) 76.92%,
      rgba(253, 253, 253, 0) 100%
    );
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
  }

  .button-container {
    display: flex;
    gap: 12px;
    width: 100%;
  }

  .easy-register-button {
    flex: 1;
    height: 60px;
    border: none;
    border-radius: 10px;
    background: var(--teeshot-green, #004b49);
    color: white;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background: #003a38;
    }

    &:active {
      background: #002927;
    }
  }

  .member-register-button {
    flex: 1;
    height: 60px;
    border: 1px solid var(--teeshot-green, #004b49);
    border-radius: 10px;
    background: white;
    color: var(--teeshot-green, #004b49);
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #f8f9fa;
    }

    &:active {
      background: #e9ecef;
    }
  }

  .modern-section {
    padding: 16px;
  }

  .member-add-section {
    margin-top: 8px;
  }
  .member-add-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .form-row {
    display: flex;
    gap: 12px;
    align-items: flex-end;
  }
  .form-item {
    flex: 1;
  }
`;

const IconCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadMembersOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0; /* 전체 화면 커버 */
  background: rgba(0, 0, 0, 0.5);
  z-index: 10999999;
  pointer-events: auto;
`;

const MemberRegisterOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10999999;
  pointer-events: auto;
`;

const MemberRegisterBottomSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 11000000;
  display: flex;
  justify-content: center;
  pointer-events: auto;
`;

const MemberRegisterContent = styled.div`
  width: 100%;
  background: var(--teeshot-white, #fff);
  border-radius: 10px 10px 0 0;
  padding: 24px 0 24px 0;
  position: relative;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  animation: slideUpFromBottom 0.3s ease-out;
  pointer-events: auto;

  @keyframes slideUpFromBottom {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    padding: 0 20px;
  }

  .modal-title {
    color: var(--teeshot-black, #1a1a1a);
    font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
    margin: 0;
    width: auto;
    height: auto;
  }

  .close-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f0f0f0;
    }
  }

  .form-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 48px;
    padding: 0 20px;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    &.contact-field {
      margin-bottom: 8px;
    }
  }

  .field-label {
    color: var(--teeshot-black, #1a1a1a);
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
  }

  .custom-dropdown {
    position: relative;
    width: 100%;

    .dropdown-trigger {
      width: 100%;
      height: 44px;
      padding: 12px 16px;
      border: 1px solid #d1d5d8;
      border-radius: 6px;
      background: #fff;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      box-sizing: border-box;

      &:hover {
        border-color: #007bff;
      }
    }
    
    &.has-value .dropdown-trigger {
      border-color: #007bff;
    }

      .dropdown-text {
        font-size: 14px;
        line-height: 18px;
        color: #9ca3af;
        flex: 1;
      }

      .dropdown-arrow {
        transition: transform 0.2s ease;
        flex-shrink: 0;

        &.open {
          transform: rotate(180deg);
        }
      }
    }

    .dropdown-options {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: #fff;
      border: 1px solid #d1d5d8;
      border-top: none;
      border-radius: 0 0 6px 6px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 1000;
      max-height: 200px;
      overflow-y: auto;

      .dropdown-option {
        padding: 12px 16px;
        font-size: 14px;
        line-height: 18px;
        color: #1a1a1a;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background-color: #f8f9fa;
        }

        &.selected {
          background-color: #e3f2fd;
          color: #1976d2;
        }
      }
    }
  }

  .form-input {
    width: 100%;
    height: 44px;
    padding: 12px 16px;
    border: 1px solid #d1d5d8;
    border-radius: 6px;
    background: #fff;
    font-size: 14px;
    line-height: 18px;
    color: #1a1a1a;
    box-sizing: border-box;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      outline: none;
      border-color: #007bff;
    }
    
    &.has-value {
      border-color: #007bff;
    }
  }

  .register-button {
    width: calc(100% - 40px);
    height: 60px;
    background: #004b49;
    border: none;
    border-radius: 6px;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    margin: 0 20px;

    &:hover {
      background: #003a38;
    }

    &:active {
      background: #002927;
    }
  }
`;
